import React from 'react';
import { View, Text, Pressable, StyleSheet, ScrollView } from 'react-native';
import { useTranslation } from 'react-i18next';
import { usePantryName } from './usePantryName';
import { LinearGradient } from 'expo-linear-gradient';
import Feather from '@expo/vector-icons/Feather';
import { Paprika, PaprikaGradients, PaprikaSpacing } from '../../styles/theme';
import { PantryItemView } from '../../redux/pantrySelectors';
import { FoodTile } from './FoodTile';
import { FreshnessPill } from './FreshnessPill';
import { categoryLabelKey } from './categoryMeta';

interface Props {
  items: PantryItemView[];
  onCook: (itemId: string) => void;
  onEat: (item: PantryItemView) => void;
  emptyMessage?: string;
}

export function CookList({ items, onCook, onEat, emptyMessage }: Props) {
  const { t } = useTranslation();
  const pantryName = usePantryName();

  if (items.length === 0) {
    return (
      <View style={styles.empty}>
        <Text style={styles.emptyTitle}>{t('pantry.cook.nothingUrgent')}</Text>
        <Text style={styles.emptySub}>{t('pantry.cook.nothingUrgentSub')}</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.list} contentContainerStyle={styles.content}>
      {items.map((view) => (
        <View key={view.item.id} style={styles.card}>
          {/* Item display: tile + name + category */}
          <View style={styles.header}>
            <FoodTile category={view.item.category} name={view.item.name} size={42} />
            <View style={styles.meta}>
              <Text style={styles.name} numberOfLines={1}>
                {pantryName(view.item)}
              </Text>
              <View style={styles.badges}>
                <View style={styles.categoryChip}>
                  <Text style={styles.categoryLabel}>
                    {t(categoryLabelKey(view.item.category))}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          {/* Freshness on right */}
          <FreshnessPill state={view.state} days={view.days} compact={false} />

          {/* Action buttons */}
          <View style={styles.actions}>
            {/* Cook this — gradient CTA */}
            <LinearGradient
              colors={PaprikaGradients.cookCta as [string, string]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={styles.cookButton}
            >
              <Pressable
                onPress={() => onCook(view.item.id)}
                style={styles.cookButtonInner}
              >
                <Text style={styles.cookButtonText}>{t('pantry.cook.cookThis')}</Text>
              </Pressable>
            </LinearGradient>

            {/* Eaten — ghost button */}
            <Pressable
              onPress={() => onEat(view)}
              style={styles.eatenButton}
            >
              <Feather name="check" size={16} color={Paprika.textMuted} />
              <Text style={styles.eatenButtonText}>{t('pantry.cook.eaten')}</Text>
            </Pressable>
          </View>
        </View>
      ))}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  list: {
    flex: 1,
  },
  content: {
    padding: PaprikaSpacing.md,
    gap: PaprikaSpacing.md,
    paddingBottom: PaprikaSpacing.lg,
  },
  empty: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: PaprikaSpacing.md,
    gap: PaprikaSpacing.sm,
  },
  emptyTitle: {
    fontFamily: 'Inter-Medium',
    fontSize: 18,
    color: Paprika.textPrimary,
    textAlign: 'center',
  },
  emptySub: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    color: Paprika.textMuted,
    textAlign: 'center',
  },
  card: {
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: 12,
    padding: PaprikaSpacing.md,
    gap: PaprikaSpacing.md,
  },
  header: {
    flexDirection: 'row',
    gap: PaprikaSpacing.md,
    alignItems: 'flex-start',
  },
  meta: {
    flex: 1,
    gap: 4,
  },
  name: {
    fontFamily: 'Inter-Medium',
    fontSize: 16,
    color: Paprika.textPrimary,
  },
  badges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4,
  },
  categoryChip: {
    backgroundColor: Paprika.surfaceLow,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  categoryLabel: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textSecondary,
  },
  actions: {
    flexDirection: 'row',
    gap: PaprikaSpacing.sm,
  },
  cookButton: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
  },
  cookButtonInner: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cookButtonText: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: '#ffffff',
  },
  eatenButton: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: Paprika.surfaceLowest,
    borderWidth: 1,
    borderColor: Paprika.hairline,
    flexDirection: 'row',
    gap: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  eatenButtonText: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: Paprika.textMuted,
  },
});
